/*
  CTA header: mega menu (hover intent + click + keyboard), top selector tabs,
  and the compact "scrolled" state. Mobile drawer, live search and cart count
  stay with static-header.js.
*/
(() => {
  const OPEN_DELAY = 120;
  const CLOSE_DELAY = 250;
  const TAB_DELAY = 90;

  function initHeader(root) {
    if (!root || root.dataset.ctaReady) return;
    root.dataset.ctaReady = 'true';

    const triggers = Array.from(root.querySelectorAll('[data-cta-mega-trigger]'));
    const hoverOpen = root.dataset.hoverOpen === 'true' && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    let openId = null;
    let openTimer = null;
    let closeTimer = null;

    const panelFor = (trigger) => document.getElementById(trigger.dataset.ctaMegaTrigger);
    const triggerFor = (id) => triggers.find((t) => t.dataset.ctaMegaTrigger === id);

    function open(trigger) {
      clearTimeout(closeTimer);
      const id = trigger.dataset.ctaMegaTrigger;
      if (openId === id) return;
      if (openId) close(false);
      const panel = panelFor(trigger);
      if (!panel) return;
      panel.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      openId = id;
    }

    function close(restoreFocus) {
      clearTimeout(openTimer);
      if (!openId) return;
      const trigger = triggerFor(openId);
      const panel = document.getElementById(openId);
      if (panel) panel.hidden = true;
      if (trigger) {
        trigger.setAttribute('aria-expanded', 'false');
        if (restoreFocus) trigger.focus();
      }
      openId = null;
    }

    triggers.forEach((trigger) => {
      trigger.addEventListener('click', () => {
        if (openId === trigger.dataset.ctaMegaTrigger) {
          close(false);
        } else {
          open(trigger);
        }
      });

      trigger.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          open(trigger);
          const first = panelFor(trigger)?.querySelector('[role="tab"][aria-selected="true"], a');
          first?.focus();
        }
      });

      if (hoverOpen) {
        trigger.addEventListener('mouseenter', () => {
          clearTimeout(openTimer);
          clearTimeout(closeTimer);
          openTimer = setTimeout(() => open(trigger), openId ? 0 : OPEN_DELAY);
        });
        trigger.addEventListener('mouseleave', () => clearTimeout(openTimer));
      }
    });

    if (hoverOpen) {
      root.addEventListener('mouseleave', () => {
        clearTimeout(openTimer);
        closeTimer = setTimeout(() => close(false), CLOSE_DELAY);
      });
      root.addEventListener('mouseenter', () => clearTimeout(closeTimer));
      root.querySelectorAll('.cta-hdr__link:not([data-cta-mega-trigger])').forEach((link) => {
        link.addEventListener('mouseenter', () => {
          clearTimeout(openTimer);
          closeTimer = setTimeout(() => close(false), CLOSE_DELAY);
        });
      });
    }

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && openId) close(true);
    });

    document.addEventListener('click', (event) => {
      if (openId && !root.contains(event.target)) close(false);
    });

    root.addEventListener('focusout', (event) => {
      if (openId && event.relatedTarget && !root.contains(event.relatedTarget)) close(false);
    });

    // Selector tabs
    root.querySelectorAll('[role="tablist"]').forEach((list) => {
      const tabs = Array.from(list.querySelectorAll('[data-cta-mega-tab]'));
      let tabTimer = null;

      const select = (tab, focus) => {
        tabs.forEach((t) => {
          const selected = t === tab;
          t.setAttribute('aria-selected', String(selected));
          t.tabIndex = selected ? 0 : -1;
          const pane = document.getElementById(t.getAttribute('aria-controls'));
          if (pane) pane.hidden = !selected;
        });
        if (focus) tab.focus();
      };

      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => select(tab, false));
        if (hoverOpen) {
          tab.addEventListener('mouseenter', () => {
            clearTimeout(tabTimer);
            tabTimer = setTimeout(() => select(tab, false), TAB_DELAY);
          });
          tab.addEventListener('mouseleave', () => clearTimeout(tabTimer));
        }
        tab.addEventListener('keydown', (event) => {
          let next = null;
          if (event.key === 'ArrowRight') next = tabs[(index + 1) % tabs.length];
          if (event.key === 'ArrowLeft') next = tabs[(index - 1 + tabs.length) % tabs.length];
          if (event.key === 'Home') next = tabs[0];
          if (event.key === 'End') next = tabs[tabs.length - 1];
          if (next) {
            event.preventDefault();
            select(next, true);
          }
        });
      });
    });

    // Theme editor: show the panel of a selected mega block
    document.addEventListener('shopify:block:select', (event) => {
      const tab = root.querySelector(`[data-cta-mega-tab][data-shopify-editor-block*="${event.detail.blockId}"]`);
      const pane = tab ? null : root.querySelector(`.cta-mega__pane[data-shopify-editor-block*="${event.detail.blockId}"]`);
      const panel = (tab || pane)?.closest('[data-cta-mega]');
      const trigger = panel && triggerFor(panel.id);
      if (trigger) open(trigger);
      if (tab) tab.click();
    });

    // Search panel
    const search = root.querySelector('[data-cta-search]');
    if (search) {
      const openers = Array.from(root.querySelectorAll('[data-cta-search-open]'));
      const input = search.querySelector('[data-cta-search-input]');
      const empty = search.querySelector('[data-cta-search-empty]');
      const results = search.querySelector('[data-cta-search-results]');
      const predictiveUrl = search.dataset.predictiveUrl || '/search/suggest';
      const searchUrl = search.dataset.searchUrl || '/search';
      let lastOpener = null;
      let fetchTimer = null;
      let requestId = 0;
      let lastQuery = '';

      const esc = (value) => String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
      const icon = (paths, size = 16) => `<svg class="cta-icon" width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${paths}</svg>`;

      const setExpanded = (value) => openers.forEach((b) => b.setAttribute('aria-expanded', String(value)));

      const openSearch = (opener) => {
        close(false);
        lastOpener = opener || null;
        const bar = root.querySelector('.cta-hdr__bar');
        if (bar) root.style.setProperty('--cta-search-top', `${Math.round(bar.getBoundingClientRect().bottom)}px`);
        renderRecent();
        search.hidden = false;
        setExpanded(true);
        document.documentElement.classList.add('cta-search-open');
        requestAnimationFrame(() => input?.focus({ preventScroll: true }));
      };

      const closeSearch = (restoreFocus) => {
        if (search.hidden) return;
        search.hidden = true;
        setExpanded(false);
        document.documentElement.classList.remove('cta-search-open');
        if (restoreFocus && lastOpener) lastOpener.focus();
      };

      openers.forEach((btn) => btn.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (search.hidden) openSearch(btn); else closeSearch(true);
      }));

      // Recent searches (stored in this browser only)
      const RECENT_KEY = 'cta:recent-searches';
      const recentWrap = search.querySelector('[data-cta-search-recent]');
      const recentList = search.querySelector('[data-cta-search-recent-list]');
      const readRecent = () => {
        try {
          return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]').filter((t) => typeof t === 'string');
        } catch (e) {
          return [];
        }
      };
      const renderRecent = () => {
        if (!recentWrap || !recentList) return;
        const items = readRecent();
        recentWrap.hidden = items.length === 0;
        recentList.innerHTML = items.map((term, i) => `<li style="--i: ${i};"><a class="cta-search__pill cta-search__pill--recent" href="${esc(`${searchUrl}?q=${encodeURIComponent(term)}&type=product`)}" data-cta-search-remember="${esc(term)}">${icon('<path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/><path d="M12 7v5l4 2"/>', 14)}${esc(term)}</a></li>`).join('');
      };
      const remember = (term) => {
        const value = String(term || '').trim();
        if (value.length < 2) return;
        const items = [value, ...readRecent().filter((t) => t.toLowerCase() !== value.toLowerCase())].slice(0, 5);
        try {
          localStorage.setItem(RECENT_KEY, JSON.stringify(items));
        } catch (e) {
          /* storage unavailable */
        }
      };
      renderRecent();

      search.querySelector('[data-cta-search-clear-recent]')?.addEventListener('click', () => {
        try {
          localStorage.removeItem(RECENT_KEY);
        } catch (e) {
          /* storage unavailable */
        }
        renderRecent();
        input?.focus();
      });

      search.querySelector('[data-cta-search-form]')?.addEventListener('submit', () => remember(input?.value));

      search.addEventListener('click', (event) => {
        const link = event.target.closest('a');
        if (!link || !search.contains(link)) return;
        if (link.dataset.ctaSearchRemember) {
          remember(link.dataset.ctaSearchRemember);
        } else if (link.closest('[data-cta-search-results]')) {
          remember(input?.value);
        }
      });

      // The x clears the field first, then closes
      const closeBtn = search.querySelector('[data-cta-search-close]');
      const syncCloseLabel = () => {
        if (!closeBtn) return;
        closeBtn.setAttribute('aria-label', input?.value ? closeBtn.dataset.clearLabel : closeBtn.dataset.closeLabel);
      };
      closeBtn?.addEventListener('click', () => {
        if (input?.value) {
          input.value = '';
          input.dispatchEvent(new Event('input', { bubbles: true }));
          input.focus();
        } else {
          closeSearch(true);
        }
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !search.hidden) closeSearch(true);
      });

      document.addEventListener('click', (event) => {
        if (!search.hidden && !search.contains(event.target) && !event.target.closest('[data-cta-search-open]')) closeSearch(false);
      });

      // Opening a mega panel closes search
      triggers.forEach((t) => t.addEventListener('mouseenter', () => { if (hoverOpen) closeSearch(false); }));
      triggers.forEach((t) => t.addEventListener('click', () => closeSearch(false)));

      const showEmpty = () => {
        results.hidden = true;
        results.innerHTML = '';
        renderRecent();
        empty.hidden = false;
        lastQuery = '';
      };

      const showLoading = (query) => {
        if (!results.hidden) return;
        results.innerHTML = `<div><p class="cta-search__label">Suggestions</p></div><div><p class="cta-search__label">Products</p><ul class="cta-search__products">${'<li><div class="cta-search__skeleton"></div></li>'.repeat(4)}</ul></div>`;
        empty.hidden = true;
        results.hidden = false;
        lastQuery = query;
      };

      const runQuery = async (query) => {
        const id = ++requestId;
        const params = new URLSearchParams({
          q: query,
          section_id: 'static-predictive-search',
          'resources[type]': 'product,query,collection',
          'resources[limit]': '5',
          'resources[limit_scope]': 'each',
          'resources[options][fields]': 'title,product_type,variants.sku,variants.title,vendor,tag',
          'resources[options][unavailable_products]': 'last',
        });
        try {
          const response = await fetch(`${predictiveUrl}?${params}`);
          if (!response.ok) throw new Error(String(response.status));
          const html = await response.text();
          if (id !== requestId || input.value.trim() !== query) return;
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const content = doc.querySelector('[data-cta-predictive]');
          if (!content) throw new Error('empty');
          results.innerHTML = content.innerHTML;
          empty.hidden = true;
          results.hidden = false;
        } catch (error) {
          if (id !== requestId) return;
          results.innerHTML = '<div></div><div><p class="cta-search__none">Search is unavailable right now. Press Enter to see all results.</p></div>';
          empty.hidden = true;
          results.hidden = false;
        }
      };

      input?.addEventListener('input', () => {
        syncCloseLabel();
        const query = input.value.trim();
        clearTimeout(fetchTimer);
        if (query.length < 2) {
          requestId += 1;
          showEmpty();
          return;
        }
        if (query !== lastQuery) showLoading(query);
        fetchTimer = setTimeout(() => runQuery(query), 180);
      });

      // Arrow keys move through suggestions and products
      search.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
        const items = Array.from(search.querySelectorAll('[data-cta-search-results]:not([hidden]) a, [data-cta-search-empty]:not([hidden]) a'));
        if (!items.length) return;
        event.preventDefault();
        const index = items.indexOf(document.activeElement);
        if (event.key === 'ArrowDown') {
          (items[index + 1] || items[0]).focus();
        } else if (index <= 0) {
          input.focus();
        } else {
          items[index - 1].focus();
        }
      });
    }

    // Compact bar once the page scrolls past the header
    const siteHeader = root.closest('.site-header');
    const target = document.querySelector('[data-header-intersection-target]');
    if (siteHeader && target && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver(([entry]) => {
        siteHeader.classList.toggle('is-scrolled', !entry.isIntersecting && entry.boundingClientRect.top < 0);
      });
      observer.observe(target);
    }
  }

  const boot = () => document.querySelectorAll('[data-cta-header]').forEach(initHeader);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
